import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  ConfigProvider,
  Form,
  Modal,
  Select,
  Spin,
  Table,
  Upload,
  message,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import style from "css/admin/component.module.css";
import axios from "axios";

const { Dragger } = Upload;

function ExcelUploadModal({ open, onCancel, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [periodList, setPeriodList] = useState([]);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setFile(null);
    setResult(null);
    setPeriodLoading(true);
    axios
      .get("/period", { params: {} })
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setPeriodList(res.data);
        }
      })
      .catch(() => setPeriodList([]))
      .finally(() => setPeriodLoading(false));
  }, [open, form]);

  const closeHandler = () => {
    form.resetFields();
    setFile(null);
    setResult(null);
    onCancel();
  };

  const beforeUpload = useCallback((f) => {
    const isXlsx = f.name?.endsWith(".xlsx");
    if (!isXlsx) {
      message.error("xlsx 파일만 업로드 가능합니다.");
      return Upload.LIST_IGNORE;
    }
    setFile(f);
    setResult(null);
    return false;
  }, []);

  const onRemove = useCallback(() => {
    setFile(null);
    setResult(null);
  }, []);

  const handleUpload = async () => {
    try {
      const values = await form.validateFields();
      if (!file) {
        message.warning("파일을 선택해주세요.");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);
      formData.append("PERIOD_ID", values.periodId);

      setLoading(true);
      const res = await axios.post("/application/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.status === 200 && res.data) {
        const d = res.data;
        setResult(d);
        if (d.status === "FAIL") {
          message.error(d.message || "업로드에 실패했습니다.");
        } else {
          message.success(
            `처리 완료 (총 ${d.total || 0}건: 성공 ${d.successCnt || 0}, 스킵 ${d.skipCnt || 0}, 실패 ${d.failCnt || 0})`,
          );
          if ((d.failCnt || 0) === 0) {
            if (typeof onSuccess === "function") onSuccess(d);
          }
        }
      }
    } catch (e) {
      if (e?.errorFields) {
        message.error("필수 항목을 확인해주세요.");
        return;
      }
      const msg =
        e?.response?.data ?? e?.message ?? "업로드 중 오류가 발생했습니다.";
      message.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const errorColumns = [
    {
      title: "행 번호",
      dataIndex: "row",
      key: "row",
      width: 80,
      align: "center",
      render: (v) => <span className={style.table_cell}>{v}</span>,
    },
    {
      title: "학번",
      dataIndex: "memberId",
      key: "memberId",
      width: 120,
      render: (v) => <span className={style.table_cell}>{v || "-"}</span>,
    },
    {
      title: "오류 내용",
      dataIndex: "message",
      key: "message",
      render: (v) => <span className={style.table_cell_error}>{v || "-"}</span>,
    },
  ];

  return (
    <Modal
      title={null}
      open={open}
      onCancel={closeHandler}
      closable={false}
      maskClosable={!loading}
      footer={
        <ConfigProvider
          theme={{
            token: { colorPrimary: "#527ff2" },
            components: {
              Button: {
                colorPrimaryHover: "#4a72de",
                colorPrimaryActive: "#3f66c8",
              },
            },
          }}
        >
          <div className={style.modal_footer_wrap}>
            <Button
              key="cancel"
              onClick={closeHandler}
              disabled={loading}
              className={style.modal_footer_btn}
            >
              취소
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={handleUpload}
              loading={loading}
              disabled={!file}
              className={style.modal_footer_btn}
            >
              저장
            </Button>
          </div>
        </ConfigProvider>
      }
      styles={{
        content: { borderRadius: 12 },
        mask: { backdropFilter: "none" },
      }}
      width={640}
      centered
    >
      <Spin spinning={loading}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            padding: "8px 0",
          }}
        >
          {/* 타이틀 + 설명 */}
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                fontFamily: "Pretendard",
                color: "#33353b",
                marginBottom: 8,
              }}
            >
              엑셀 파일 일괄 등록
            </div>
            <div
              style={{
                fontSize: 14,
                fontFamily: "Pretendard",
                color: "#797d88",
                lineHeight: 1.6,
              }}
            >
              엑셀을 업로드하여 다수의 사용자 정보를 한 번에 입력할 수 있습니다.
              <br />
              정해진 엑셀 양식에 입력하여 업로드하세요. (지원 파일 양식: xlsx)
            </div>
            <span
              style={{
                fontSize: 14,
                fontFamily: "Pretendard",
                color: "#527ff2",
                textDecoration: "underline",
                marginTop: 4,
                display: "inline-block",
                cursor: "pointer",
              }}
              onClick={() => {
                axios
                  .get("/application/template", { responseType: "blob" })
                  .then((res) => {
                    const url = window.URL.createObjectURL(
                      new Blob([res.data]),
                    );
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", "신청_양식.xlsx");
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  })
                  .catch(() => {
                    message.error("양식 다운로드에 실패했습니다.");
                  });
              }}
            >
              엑셀 양식 다운로드
            </span>
          </div>

          {/* 드래그앤드롭 업로드 영역 */}
          <Dragger
            beforeUpload={beforeUpload}
            onRemove={onRemove}
            fileList={file ? [file] : []}
            maxCount={1}
            accept=".xlsx"
            style={{ borderRadius: 8 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ fontSize: 36, color: "#527ff2" }} />
            </p>
            <p
              style={{
                fontSize: 14,
                fontFamily: "Pretendard",
                color: "#797d88",
              }}
            >
              클릭 또는 드래그해서 파일을 선택해 주세요.
            </p>
          </Dragger>

          {/* 신청기간 목록 */}
          <Form form={form} requiredMark={false}>
            <div className={style.form_row}>
              <div className={style.form_label}>신청기간 목록</div>
              <Form.Item
                name="periodId"
                rules={[{ required: true, message: "신청기간을 선택하세요" }]}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  className={style.search_cascader}
                  placeholder={
                    periodLoading ? "목록 불러오는 중..." : "선택하세요"
                  }
                  loading={periodLoading}
                  disabled={periodLoading}
                  options={(periodList || []).map((p) => ({
                    value: p.PERIOD_ID,
                    label: p.PERIOD_NM || p.PERIOD_ID,
                  }))}
                />
              </Form.Item>
            </div>
          </Form>

          {/* 결과 표시 */}
          {result && result.status !== "FAIL" && (
            <div>
              <div className={style.result_summary} style={{ marginBottom: 8 }}>
                <span>
                  총 <b>{result.total || 0}</b>건
                </span>
                <span style={{ color: "#527ff2" }}>
                  성공 <b>{result.successCnt || 0}</b>건
                </span>
                <span style={{ color: "#797d88" }}>
                  스킵 <b>{result.skipCnt || 0}</b>건
                </span>
                <span style={{ color: "#f65c22" }}>
                  실패 <b>{result.failCnt || 0}</b>건
                </span>
              </div>

              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <Table
                  rowKey={(row) => `${row?.row}_${row?.memberId}`}
                  columns={errorColumns}
                  dataSource={result.errors}
                  size="small"
                  pagination={{ pageSize: 5, showSizeChanger: false }}
                  scroll={{ y: 200 }}
                />
              )}
            </div>
          )}
        </div>
      </Spin>
    </Modal>
  );
}

export default ExcelUploadModal;
